import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface BadgeToggleOption {
  label: string
  value: string
}

export interface BadgeToggleGroupProps {
  /** Heading above the badges. */
  label?: string
  options: BadgeToggleOption[]
  value: string[]
  onChange: (value: string[]) => void
  loading?: boolean
  /** Shown when there is nothing to pick from. */
  emptyText?: string
  disabled?: boolean
  className?: string
}

/**
 * A row of badges that toggle on and off — a multi-select for a handful of
 * known values, where seeing every option at once beats opening a dropdown.
 */
export function BadgeToggleGroup({
  label,
  options,
  value,
  onChange,
  loading,
  emptyText,
  disabled,
  className,
}: BadgeToggleGroupProps) {
  const { t } = useTranslation()
  const selected = new Set(value)

  const toggle = (option: string) =>
    onChange(selected.has(option) ? value.filter((v) => v !== option) : [...value, option])

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && (
        <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
          {label}
        </span>
      )}
      {loading && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
      {!loading && options.length === 0 && (
        <p className="text-xs text-muted-foreground">{emptyText ?? t('targeting.noResults')}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {!loading &&
          options.map((option) => (
            <Button
              key={option.value}
              type="button"
              size="sm"
              disabled={disabled}
              variant={selected.has(option.value) ? 'default' : 'outline'}
              aria-pressed={selected.has(option.value)}
              // The border is on both states, transparent when filled, so a
              // badge keeps its width as it is toggled.
              className={cn(
                'h-7 rounded-full border px-3 text-xs font-medium',
                selected.has(option.value) && 'border-transparent'
              )}
              onClick={() => toggle(option.value)}
            >
              {option.label}
            </Button>
          ))}
      </div>
    </div>
  )
}
